import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { createRecord, LARK_TABLES } from "./larkService_v2";

const EmployeeForm = ({ userId, onFormSubmitted }) => {
  const [loading, setLoading] = useState(false);
  
  // Persistence Helpers
  const getSaved = (key, fallback) => {
    const saved = localStorage.getItem(key);
    try { return saved ? JSON.parse(saved) : fallback; } catch (e) { return fallback; }
  };

  const [personalDetails, setPersonalDetails] = useState(() => getSaved("hr_personal_details", {
    name: "", mobile: "", emergency: "", email: "", dob: "", gender: "",
    bloodGroup: "", maritalStatus: "", anniversary: "", nationality: "",
    religion: "", permanentAddress: "", communicationAddress: "", aadhar: "", pan: ""
  }));

  const [bankDetails, setBankDetails] = useState(() => getSaved("hr_bank_details", {
    bankName: "", accountHolder: "", accountNumber: "", ifscCode: ""
  }));

  const [idDetails, setIdDetails] = useState(() => getSaved("hr_id_details", {
    pfUan: "", esic: "", drivingLicense: ""
  }));

  const [qualifications, setQualifications] = useState(() => getSaved("hr_qualifications", [{
    qualification: "", board: "", school: "", subject: "", passingYear: "", percentage: ""
  }]));

  const [additionalDetails, setAdditionalDetails] = useState(() => getSaved("hr_additional_details", {
    awards: ["", "", ""], training: ["", "", ""]
  }));

  const [experience, setExperience] = useState(() => getSaved("hr_experience", [{
    company: "", designation: "", startDate: "", endDate: "", salary: ""
  }]));

  const [healthDetails, setHealthDetails] = useState(() => localStorage.getItem("hr_health_details") || "");

  // Sync with localStorage
  React.useEffect(() => { localStorage.setItem("hr_personal_details", JSON.stringify(personalDetails)); }, [personalDetails]);
  React.useEffect(() => { localStorage.setItem("hr_bank_details", JSON.stringify(bankDetails)); }, [bankDetails]);
  React.useEffect(() => { localStorage.setItem("hr_id_details", JSON.stringify(idDetails)); }, [idDetails]);
  React.useEffect(() => { localStorage.setItem("hr_qualifications", JSON.stringify(qualifications)); }, [qualifications]);
  React.useEffect(() => { localStorage.setItem("hr_additional_details", JSON.stringify(additionalDetails)); }, [additionalDetails]);
  React.useEffect(() => { localStorage.setItem("hr_experience", JSON.stringify(experience)); }, [experience]);
  React.useEffect(() => { localStorage.setItem("hr_health_details", healthDetails); }, [healthDetails]);

  const handlePersonalChange = (e) => setPersonalDetails({ ...personalDetails, [e.target.name]: e.target.value });
  const handleBankChange = (e) => setBankDetails({ ...bankDetails, [e.target.name]: e.target.value });
  const handleIdChange = (e) => setIdDetails({ ...idDetails, [e.target.name]: e.target.value });

  const addQualification = () => setQualifications([...qualifications, {
    qualification: "", board: "", school: "", subject: "", passingYear: "", percentage: ""
  }]);

  const updateQualification = (index, field, value) => {
    const updated = [...qualifications];
    updated[index][field] = value;
    setQualifications(updated);
  };

  const addExperience = () => setExperience([...experience, {
    company: "", designation: "", startDate: "", endDate: "", salary: ""
  }]);

  const updateExperience = (index, field, value) => {
    const updated = [...experience];
    updated[index][field] = value;
    setExperience(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = {
        userId,
        name: personalDetails.name,
        email: personalDetails.email,
        personalDetails,
        bankDetails,
        idDetails,
        qualifications,
        experience,
        additionalDetails,
        healthDetails,
        createdAt: Date.now()
      };

      await createRecord(LARK_TABLES.CANDIDATE_INFO, {
        "User ID": userId,
        "Form JSON": JSON.stringify(formData)
      });
      
      // Clear persistence on success!
      ["hr_personal_details", "hr_bank_details", "hr_id_details", "hr_qualifications", "hr_additional_details", "hr_experience", "hr_health_details"].forEach(k => localStorage.removeItem(k));
      
      toast.success("Form submitted successfully!");
      onFormSubmitted(personalDetails.name);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="employee-form-inner">
      <form onSubmit={handleSubmit}>
        
        {/* Section 1: Personal Details */}
        <section className="form-section">
          <h3>1. PERSONAL DETAILS</h3>
          <div className="form-grid">
            <div className="form-group"><label>Name</label><input name="name" value={personalDetails.name} onChange={handlePersonalChange} /></div>
            <div className="form-group"><label>Mobile Number</label><input name="mobile" value={personalDetails.mobile} onChange={handlePersonalChange} /></div>
            <div className="form-group"><label>Emergency Number</label><input name="emergency" value={personalDetails.emergency} onChange={handlePersonalChange} /></div>
            <div className="form-group"><label>Email ID</label><input name="email" value={personalDetails.email} onChange={handlePersonalChange} /></div>
            <div className="form-group"><label>Birth Date</label><input type="date" name="dob" value={personalDetails.dob} onChange={handlePersonalChange} /></div>
            <div className="form-group"><label>Gender</label><input name="gender" value={personalDetails.gender} onChange={handlePersonalChange} /></div>
            <div className="form-group"><label>Blood Group</label><input name="bloodGroup" value={personalDetails.bloodGroup} onChange={handlePersonalChange} /></div>
            <div className="form-group"><label>Marital Status</label><input name="maritalStatus" value={personalDetails.maritalStatus} onChange={handlePersonalChange} /></div>
            <div className="form-group"><label>Anniversary Date</label><input type="date" name="anniversary" value={personalDetails.anniversary} onChange={handlePersonalChange} /></div>
            <div className="form-group"><label>Nationality</label><input name="nationality" value={personalDetails.nationality} onChange={handlePersonalChange} /></div>
            <div className="form-group"><label>Religion</label><input name="religion" value={personalDetails.religion} onChange={handlePersonalChange} /></div>
            <div className="form-group"><label>Aadhar Number</label><input name="aadhar" value={personalDetails.aadhar} onChange={handlePersonalChange} /></div>
            <div className="form-group"><label>PAN Number</label><input name="pan" value={personalDetails.pan} onChange={handlePersonalChange} /></div>
          </div>
          <div className="form-group full-width"><label>Permanent Address</label><textarea name="permanentAddress" value={personalDetails.permanentAddress} onChange={handlePersonalChange} /></div>
          <div className="form-group full-width"><label>Communication Address</label><textarea name="communicationAddress" value={personalDetails.communicationAddress} onChange={handlePersonalChange} /></div>
        </section>

        {/* Section 2: Bank Details */}
        <section className="form-section">
          <h3>2. BANK DETAILS</h3>
          <div className="form-grid">
            <div className="form-group"><label>Bank Name</label><input name="bankName" value={bankDetails.bankName} onChange={handleBankChange} /></div>
            <div className="form-group"><label>Account Holder Name</label><input name="accountHolder" value={bankDetails.accountHolder} onChange={handleBankChange} /></div>
            <div className="form-group"><label>Account Number</label><input name="accountNumber" value={bankDetails.accountNumber} onChange={handleBankChange} /></div>
            <div className="form-group"><label>IFSC Code</label><input name="ifscCode" value={bankDetails.ifscCode} onChange={handleBankChange} /></div>
          </div>
        </section>

        {/* Section 3: Identification */}
        <section className="form-section">
          <h3>3. IDENTIFICATION DETAILS</h3>
          <div className="form-grid">
            <div className="form-group"><label>PF / UAN Number</label><input name="pfUan" value={idDetails.pfUan} onChange={handleIdChange} /></div>
            <div className="form-group"><label>ESIC Number</label><input name="esic" value={idDetails.esic} onChange={handleIdChange} /></div>
            <div className="form-group"><label>Driving License No.</label><input name="drivingLicense" value={idDetails.drivingLicense} onChange={handleIdChange} /></div>
          </div>
        </section>

        {/* Section 4: Qualification */}
        <section className="form-section">
          <h3>4. QUALIFICATION DETAILS</h3>
          <div className="table-responsive">
            <table className="form-table">
              <thead>
                <tr>
                  <th>Qualification</th>
                  <th>Board / University</th>
                  <th>School / College</th>
                  <th>Subject</th>
                  <th>Passing Year</th>
                  <th>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {qualifications.map((q, i) => (
                  <tr key={i}>
                    <td><input value={q.qualification} onChange={(e) => updateQualification(i, "qualification", e.target.value)} /></td>
                    <td><input value={q.board} onChange={(e) => updateQualification(i, "board", e.target.value)} /></td>
                    <td><input value={q.school} onChange={(e) => updateQualification(i, "school", e.target.value)} /></td>
                    <td><input value={q.subject} onChange={(e) => updateQualification(i, "subject", e.target.value)} /></td>
                    <td><input value={q.passingYear} onChange={(e) => updateQualification(i, "passingYear", e.target.value)} /></td>
                    <td><input value={q.percentage} onChange={(e) => updateQualification(i, "percentage", e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="add-row-btn" onClick={addQualification}>+ Add More Qualification</button>
        </section>

        {/* Section 5: Additional Details */}
        <section className="form-section">
          <h3>5. ADDITIONAL DETAILS</h3>
          <div className="form-grid two-col">
            <div className="detail-list">
              <label>Awards / Scholarships</label>
              {additionalDetails.awards.map((a, i) => (
                <input key={i} value={a} onChange={(e) => {
                  const updated = [...additionalDetails.awards];
                  updated[i] = e.target.value;
                  setAdditionalDetails({ ...additionalDetails, awards: updated });
                }} placeholder={`Award ${i+1}`} />
              ))}
            </div>
            <div className="detail-list">
              <label>Training Courses</label>
              {additionalDetails.training.map((t, i) => (
                <input key={i} value={t} onChange={(e) => {
                  const updated = [...additionalDetails.training];
                  updated[i] = e.target.value;
                  setAdditionalDetails({ ...additionalDetails, training: updated });
                }} placeholder={`Course ${i+1}`} />
              ))}
            </div>
          </div>
        </section>

        {/* Section 6: Work Experience */}
        <section className="form-section">
          <h3>6. WORK EXPERIENCE</h3>
          <div className="table-responsive">
            <table className="form-table">
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Designation</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Salary</th>
                </tr>
              </thead>
              <tbody>
                {experience.map((exp, i) => (
                  <tr key={i}>
                    <td><input value={exp.company} onChange={(e) => updateExperience(i, "company", e.target.value)} /></td>
                    <td><input value={exp.designation} onChange={(e) => updateExperience(i, "designation", e.target.value)} /></td>
                    <td><input type="date" value={exp.startDate} onChange={(e) => updateExperience(i, "startDate", e.target.value)} /></td>
                    <td><input type="date" value={exp.endDate} onChange={(e) => updateExperience(i, "endDate", e.target.value)} /></td>
                    <td><input value={exp.salary} onChange={(e) => updateExperience(i, "salary", e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="add-row-btn" onClick={addExperience}>+ Add More Experience</button>
        </section>

        {/* Section 7: Health Details */}
        <section className="form-section">
          <h3>7. HEALTH DETAILS</h3>
          <div className="form-group full-width">
            <label>Any health issue</label>
            <textarea value={healthDetails} onChange={(e) => setHealthDetails(e.target.value)} />
          </div>
        </section>

        <button type="submit" className="submit-form-btn" disabled={loading}>
          {loading ? "Submitting..." : "Submit Form"}
        </button>
      </form>
    </div>
  );
};

export default EmployeeForm;
